import type { ReactNode } from 'react';
import {
  BadgeIndianRupee,
  Banknote,
  Building2,
  CalendarClock,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  Contact,
  FileBarChart,
  FileText,
  FolderKanban,
  Gauge,
  HandCoins,
  Inbox,
  Handshake,
  KanbanSquare,
  KeyRound,
  Layers,
  LayoutDashboard,
  ListChecks,
  Network,
  PiggyBank,
  Receipt,
  ScrollText,
  Settings,
  SlidersHorizontal,
  Target,
  Timer,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import type { PermissionKey, PermissionScope, Terminology } from '@stoplord/shared';

export interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  perm?: PermissionKey;
  /** Visible when the user holds any one of these. */
  anyPerm?: PermissionKey[];
  scope?: PermissionScope;
  when?: 'tracksWorkTime';
  end?: boolean;
  /** Sub-menu (shown collapsible in the sidebar). The parent is visible when any child is. */
  children?: NavItem[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

const i = (n: ReactNode) => n;
const s = 'h-4 w-4';

export function buildNav(t: Terminology): NavGroup[] {
  return [
    { label: 'Overview', items: [{ label: 'Dashboard', to: '/', icon: i(<LayoutDashboard className={s} />), end: true }] },
    {
      label: 'CRM',
      items: [
        { label: t.lead.plural, to: '/crm/leads', icon: i(<Contact className={s} />), perm: 'crm.leads.view' },
        { label: 'Pipeline', to: '/crm/pipeline', icon: i(<KanbanSquare className={s} />), perm: 'crm.leads.view' },
        { label: 'Follow-ups', to: '/crm/followups', icon: i(<CalendarClock className={s} />), perm: 'crm.leads.view' },
        { label: t.client.plural, to: '/crm/clients', icon: i(<Handshake className={s} />), perm: 'crm.clients.view' },
        { label: 'Client requests', to: '/crm/client-requests', icon: i(<Inbox className={s} />), anyPerm: ['crm.clients.portal', 'projects.edit'] },
      ],
    },
    { label: 'Essentials', items: [{ label: t.task.plural, to: '/tasks', icon: i(<CheckSquare className={s} />), perm: 'tasks.view' }] },
    { label: 'Projects', items: [{ label: 'Projects', to: '/projects', icon: i(<FolderKanban className={s} />), perm: 'projects.view' }] },
    {
      label: 'HRM',
      items: [
        { label: t.employee.plural, to: '/hrm/employees', icon: i(<Users className={s} />), perm: 'hrm.employees.view', scope: 'department' },
        { label: 'Organisation', to: '/hrm/org', icon: i(<Network className={s} />), perm: 'hrm.org.manage' },
        { label: 'Work monitor', to: '/hrm/work-monitor', icon: i(<Timer className={s} />), perm: 'hrm.attendance.view', scope: 'department' },
        { label: 'My attendance', to: '/hrm/my-attendance', icon: i(<UserCheck className={s} />), when: 'tracksWorkTime' },
        { label: 'Leave', to: '/hrm/leave', icon: i(<CalendarDays className={s} />), perm: 'hrm.leave.view' },
        {
          label: 'Performance & Targets',
          to: '/sales/targets',
          icon: i(<Target className={s} />),
          children: [
            { label: 'Revenue targets', to: '/sales/targets', icon: i(<TrendingUp className={s} />), perm: 'sales.targets.view', scope: 'department' },
            { label: 'My target', to: '/sales/my-target', icon: i(<Target className={s} />), perm: 'sales.targets.view' },
          ],
        },
        {
          label: 'Salary & Payroll',
          to: '/payroll',
          icon: i(<Wallet className={s} />),
          children: [
            { label: 'Salary dashboard', to: '/payroll', icon: i(<Gauge className={s} />), anyPerm: ['payroll.run', 'payroll.approve', 'payroll.pay'], end: true },
            { label: 'Employee salary', to: '/payroll/salaries', icon: i(<Users className={s} />), perm: 'payroll.salary.view', scope: 'department' },
            { label: 'Salary structure', to: '/payroll/settings?tab=structures', icon: i(<Layers className={s} />), perm: 'payroll.settings' },
            { label: 'Monthly payroll', to: '/payroll/runs', icon: i(<ClipboardList className={s} />), anyPerm: ['payroll.run', 'payroll.approve', 'payroll.pay'] },
            { label: 'PF management', to: '/payroll/settings?tab=pf', icon: i(<PiggyBank className={s} />), perm: 'payroll.settings' },
            { label: 'Allowances & deductions', to: '/payroll/settings?tab=components', icon: i(<ListChecks className={s} />), perm: 'payroll.settings' },
            { label: 'Bonuses & incentives', to: '/sales/commissions', icon: i(<BadgeIndianRupee className={s} />), perm: 'sales.commissions.view' },
            { label: 'Salary advances', to: '/payroll/advances', icon: i(<HandCoins className={s} />), anyPerm: ['payroll.advances.request', 'payroll.advances.manage'] },
            { label: 'Payslips', to: '/payroll/me', icon: i(<Receipt className={s} />), perm: 'payroll.salary.view' },
            { label: 'Payment history', to: '/payroll/payments', icon: i(<Banknote className={s} />), anyPerm: ['payroll.pay', 'payroll.approve'] },
            { label: 'Payroll reports', to: '/payroll/reports', icon: i(<FileBarChart className={s} />), anyPerm: ['payroll.run', 'payroll.approve', 'payroll.pay'] },
            { label: 'Payroll settings', to: '/payroll/settings?tab=settings', icon: i(<SlidersHorizontal className={s} />), perm: 'payroll.settings' },
          ],
        },
      ],
    },
    {
      label: 'Sales & Business',
      items: [
        { label: 'Quotations', to: '/sales/quotations', icon: i(<FileText className={s} />), perm: 'sales.quotations.view' },
      ],
    },
    {
      label: 'Administration',
      items: [
        { label: 'Users', to: '/settings/users', icon: i(<KeyRound className={s} />), perm: 'access.users.view' },
        { label: 'Roles & permissions', to: '/settings/roles', icon: i(<Building2 className={s} />), perm: 'access.roles.view' },
        { label: 'Settings', to: '/settings', icon: i(<Settings className={s} />), perm: 'settings.view', end: true },
        { label: 'Audit log', to: '/audit-logs', icon: i(<ScrollText className={s} />), perm: 'audit.view' },
      ],
    },
  ];
}

type Can = (key: PermissionKey, minScope?: PermissionScope) => boolean;

function allowed(it: NavItem, can: Can, tracks: boolean) {
  if (it.when === 'tracksWorkTime' && !tracks) return false;
  if (it.perm && !can(it.perm, it.scope)) return false;
  if (it.anyPerm && !it.anyPerm.some((p) => can(p, it.scope))) return false;
  return true;
}

/** Permission-aware navigation: hides items (and sub-menus with no visible entries) the user cannot open. */
export function visibleNav(groups: NavGroup[], can: Can, tracks: boolean): NavGroup[] {
  return groups
    .map((g) => ({
      ...g,
      items: g.items
        .map((it) => (it.children ? { ...it, children: it.children.filter((c) => allowed(c, can, tracks)) } : it))
        .filter((it) => (it.children ? it.children.length > 0 : allowed(it, can, tracks)))
        // A sub-menu with a single entry is shown as that entry.
        .map((it) => (it.children && it.children.length === 1 ? it.children[0]! : it)),
    }))
    .filter((g) => g.items.length);
}

export function flattenNav(groups: NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items.flatMap((it) => (it.children ? it.children : [it])));
}
